import i18n, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

export const fallbackLanguage = 'en';
export const primaryLanguage = 'nl';

export interface LanguageOption {
  code: string;
  nativeName: string;
}

type LocaleTranslation = Record<string, unknown> & {
  meta?: { nativeName?: unknown };
};

const localeModules = import.meta.glob<{ default: LocaleTranslation }>('./locales/*.json', { eager: true });

function loadLocaleResources(): Resource {
  return Object.fromEntries(
    Object.entries(localeModules).map(([path, module]) => [
      path.match(/\/([^/]+)\.json$/)?.[1] ?? path,
      { translation: module.default },
    ]),
  ) as Resource;
}

export function createI18nOptions(resources: Resource) {
  const languages = getLanguageOptions(resources);
  return {
    resources,
    fallbackLng: fallbackLanguage,
    supportedLngs: languages.map(({ code }) => code),
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'xxtract-language',
      caches: ['localStorage'],
    },
  };
}

function getLocaleTranslation(resource: unknown): LocaleTranslation {
  const translation = (resource as { translation?: unknown })?.translation;
  if (typeof translation !== 'object' || translation === null) {
    throw new Error('Each locale resource needs a translation object');
  }
  return translation as LocaleTranslation;
}

export function getLanguageOptions(resources: Resource): LanguageOption[] {
  return Object.entries(resources)
    .map(([code, resource]) => {
      const nativeName = getLocaleTranslation(resource).meta?.nativeName;
      if (typeof nativeName !== 'string' || nativeName.trim() === '') {
        throw new Error(`Locale ${code} needs meta.nativeName`);
      }
      return { code, nativeName };
    })
    .sort((left, right) => {
      if (left.code === primaryLanguage) return -1;
      if (right.code === primaryLanguage) return 1;
      return left.code.localeCompare(right.code);
    });
}

export function getNextLanguageCode(currentLanguage: string, languages: LanguageOption[]): string {
  const currentCode = currentLanguage.split('-')[0];
  const currentIndex = languages.findIndex(({ code }) => code === currentCode);
  return languages[(currentIndex + 1) % languages.length]?.code ?? fallbackLanguage;
}

const resources = loadLocaleResources();
export const activeLanguages = getLanguageOptions(resources);
export const activeLanguageCodes = activeLanguages.map(({ code }) => code);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(createI18nOptions(resources));

export default i18n;
